import { format, isValid, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserAccessHistory } from '../../../api';

export function getAccessStatusLabel(active) {
  return active ? 'Vigente' : 'Expirado';
}

const ACCESS_TYPE_BADGES = {
  block: {
    label: 'Bloqueio',
    color: '#dc3545',
    backgroundColor: 'rgba(220, 53, 69, 0.12)',
  },
  release: {
    label: 'Liberação',
    color: '#01AB7D',
    backgroundColor: '#E6F7F2',
  },
};

const DEFAULT_ACCESS_TYPE_BADGE = {
  color: '#6c757d',
  backgroundColor: 'rgba(108, 117, 125, 0.12)',
};

export function getAccessTypeBadge(type) {
  return ACCESS_TYPE_BADGES[type] ?? {
    ...DEFAULT_ACCESS_TYPE_BADGE,
    label: type,
  };
}

function formatTimestamp(dateString) {
  if (!dateString) {
    return '-';
  }

  let parsed;

  if (dateString.endsWith('Z')) {
    parsed = parseISO(dateString.replace('Z', ''));
  } else {
    parsed = parseISO(dateString);
  }

  if (!isValid(parsed)) {
    return '-';
  }

  return format(parsed, 'dd/MM/yyyy HH:mm');
}

export default function UserAccessHistory({ clienteId }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  const userAccessStatus = useSelector(
    (state) => state.app.client_subscription?.userAccessStatus,
  );

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUserAccessHistory(clienteId, { limit: 1 });
      const latestRecord = response.data?.data?.[0] ?? null;
      setRecord(latestRecord);
    } catch {
      setRecord(null);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [clienteId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, userAccessStatus]);

  if (loading && !hasLoaded) {
    return null;
  }

  if (!record) {
    return (
      <div className="row mb-5">
        <div className="col-xl-4">
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '10px',
              border: '1px solid #eaeaea',
            }}
          >
            <h2 className="azul-cabecalho" style={{ lineHeight: 1 }}>
              CONTROLE DE ACESSO MANUAL
            </h2>
            <p className="campo mb-0" style={{ opacity: 0.6 }}>
              Nenhuma liberação ou bloqueio manual registrado.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const statusLabel = getAccessStatusLabel(record.active);
  const isVigente = record.active;
  const typeBadge = getAccessTypeBadge(record.type);

  return (
    <div className="row mb-5">
      <div className="col-xl-4">

        <span className="azul-cabecalho" style={{ lineHeight: 1 }}>
          Status de liberação de acesso
        </span>

        <div className="d-flex flex-row justify-content-between">
          <div>
            <p className="tipo-campo">TIPO</p>
            <p className="campo">
              <span
                className="badge font-weight-bold"
                style={{
                  color: typeBadge.color,
                  backgroundColor: typeBadge.backgroundColor,
                }}
              >
                {typeBadge.label}
              </span>
            </p>
          </div>

          <div>
            <p className="tipo-campo">OPERADOR</p>
            <p className="campo">
              {record.operator
                ? `${record.operator.name} (${record.operator.email})`
                : '-'}
            </p>
          </div>

          <div className="text-right">
            <p className="tipo-campo">STATUS</p>
            <p className="campo mb-0">
              <span
                className="badge font-weight-bold"
                style={{
                  color: isVigente ? '#01AB7D' : '#6c757d',
                  backgroundColor: isVigente ? '#E6F7F2' : 'rgba(108, 117, 125, 0.12)',
                }}
              >
                {statusLabel}
              </span>
            </p>
          </div>
        </div>


        <div className="d-flex flex-row justify-content-between">
          <div>
            <p className="tipo-campo">DATA DA AÇÃO</p>
            <p className="campo">{formatTimestamp(record.timestamp)}</p>
          </div>
          <div>
            <p className="tipo-campo">DATA DE VALIDADE</p>
            <p className="campo">{formatTimestamp(record.expires_at)}</p>
          </div>
        </div>


        <div>
          <p className="tipo-campo">MOTIVO</p>
          <p className="campo">{record.reason || '-'}</p>
        </div>

        {record.motivation && (
          <div>
            <p className="tipo-campo">MOTIVAÇÃO</p>
            <p className="campo mb-0">{record.motivation}</p>
          </div>
        )}
      </div>
    </div>
  );
}
